/**
 * board-raster.ts — draw board objects onto a canvas, for "copy as image".
 *
 * ## Why this is drawn rather than screenshotted
 *
 * The obvious implementation is html2canvas or html-to-image over the canvas
 * div. Both were rejected, for reasons specific to this board:
 *
 * - **A selection is not a rectangle of the screen.** The thing being copied is
 *   a set of objects, which may be scattered, may sit behind other objects the
 *   user did not select, and is drawn inside a pan/zoom transform. A DOM
 *   screenshot captures a region, so it would also capture whatever happens to
 *   overlap — including the selection ring, the resize handle, other people's
 *   presence cursors and any vote dots.
 * - **It would be screen resolution, not object resolution.** Objects are
 *   rendered at `view.zoom`; a board zoomed to 40% screenshots at 40%. Drawing
 *   from the model means the export is always sharp, whatever the view.
 * - **The DOM renderer's styling does not survive either library.** Notes get
 *   their fill from `--note`, set by a `.note-<color>` class and redefined
 *   under `.dark`. Both libraries have a long history of mis-resolving CSS
 *   custom properties on cloned nodes, and the failure is silent — black notes.
 *
 * So this file is a second renderer for the same model. That is a real cost:
 * every new `kind`, and every visual change to an existing one, has to be made
 * in two places or the export drifts from the screen. It is accepted because
 * the model is small and closed (eight kinds, in `types.ts`), and because the
 * alternative was a 50KB dependency that got the colors wrong.
 *
 * **If you add a `kind`, add it to `drawObject` below.** An unknown kind draws
 * nothing rather than throwing, so the symptom is a missing shape in an export,
 * not an error anyone will see.
 *
 * ## What is pure and what is not
 *
 * Everything above `drawSelection` is pure arithmetic and is asserted by
 * `scripts/board-raster-check.mjs`. Below it needs a real canvas and the
 * document's computed styles, so it is only exercised in a browser.
 */
import type { BoardObject, NoteColor } from './types'

/** Breathing room around the selection, in board units. */
export const RASTER_PADDING = 24

/**
 * Pixels per board unit. 2 gives a crisp image on a HiDPI screen and in
 * Keynote/PowerPoint, which is where these end up.
 */
export const RASTER_SCALE = 2

/**
 * Ceiling on the output, in pixels of area.
 *
 * A board is unbounded, and `⌘A` then copy is a thing people will do. 40
 * megapixels is roughly a 6300x6300 PNG — large, still encodable, and well
 * inside the ~268MP canvas area limit Safari enforces (beyond which the canvas
 * silently becomes blank rather than throwing). Past this the scale is reduced
 * rather than the image cropped: a smaller picture of everything beats a sharp
 * picture of part of it.
 */
export const MAX_RASTER_PIXELS = 40_000_000

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * How far an object's drawing extends beyond its own box, per side.
 *
 * Two kinds paint outside their row's geometry, and both would be clipped by a
 * naive union of x/y/width/height:
 *
 * - A **meeple**'s name renders *below* the token (`-bottom-6`, plus room for a
 *   second line when a name wraps).
 * - A **frame**'s title renders *above* the frame (`-top-6`).
 *
 * The numbers match the Tailwind classes in `board-canvas.tsx`. If those move,
 * these move.
 */
export function overhangOf(o: Pick<BoardObject, 'kind'>): {
  top: number
  right: number
  bottom: number
  left: number
} {
  if (o.kind === 'meeple') return { top: 0, right: 18, bottom: 34, left: 18 }
  if (o.kind === 'frame') return { top: 24, right: 0, bottom: 0, left: 0 }
  return { top: 0, right: 0, bottom: 0, left: 0 }
}

/**
 * The box one object occupies, including anything it draws outside itself.
 *
 * An arrow stores width/height as a VECTOR from (x, y) — see the arrow branch
 * in `board-canvas.tsx` — so either may be negative and the box has to be
 * normalised. Treating the vector as a size is how an arrow pointing up and
 * left produces a bounds with negative width, which then silently swallows the
 * whole union.
 */
export function objectBox(o: BoardObject): Rect {
  const over = overhangOf(o)
  let { x, y, width, height } = o

  if (o.kind === 'arrow') {
    // Normalise the vector, then allow for the stroke and the arrowhead, which
    // are drawn 12 units either side of the line's own axis.
    const x2 = x + width
    const y2 = y + height
    x = Math.min(x, x2)
    y = Math.min(y, y2)
    width = Math.abs(width)
    height = Math.abs(height)
    return { x: x - 12, y: y - 12, width: width + 24, height: height + 24 }
  }

  return {
    x: x - over.left,
    y: y - over.top,
    width: width + over.left + over.right,
    height: height + over.top + over.bottom,
  }
}

/** The union of every object's box, padded. Null for an empty selection. */
export function rasterBounds(objects: BoardObject[], padding = RASTER_PADDING): Rect | null {
  if (objects.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const o of objects) {
    const b = objectBox(o)
    minX = Math.min(minX, b.x)
    minY = Math.min(minY, b.y)
    maxX = Math.max(maxX, b.x + b.width)
    maxY = Math.max(maxY, b.y + b.height)
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null
  return {
    x: minX - padding,
    y: minY - padding,
    width: Math.max(1, maxX - minX + padding * 2),
    height: Math.max(1, maxY - minY + padding * 2),
  }
}

/**
 * Output size for a bounds, with the scale reduced if the result would be
 * enormous. Returns whole pixels — a fractional canvas size is floored by the
 * browser, which would shave the right and bottom edges.
 */
export function rasterSize(
  bounds: Rect,
  scale = RASTER_SCALE,
  maxPixels = MAX_RASTER_PIXELS,
): { width: number; height: number; scale: number } {
  const area = bounds.width * bounds.height * scale * scale
  const used = area > maxPixels ? scale * Math.sqrt(maxPixels / area) : scale
  return {
    width: Math.max(1, Math.floor(bounds.width * used)),
    height: Math.max(1, Math.floor(bounds.height * used)),
    scale: used,
  }
}

/**
 * Greedy word wrap, with a hard break for a single word too long to fit.
 *
 * `measure` is injected so the rules can be asserted without a canvas. Honours
 * newlines the user typed, because a sticky note's text is `whitespace-pre-wrap`
 * on screen and a copied image that reflowed someone's list would be wrong.
 */
export function wrapLines(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): string[] {
  const out: string[] = []
  for (const paragraph of text.split('\n')) {
    if (paragraph === '') {
      out.push('')
      continue
    }
    let line = ''
    for (const word of paragraph.split(/(\s+)/)) {
      if (word === '') continue

      if (line !== '' && measure(line + word) > maxWidth) {
        out.push(line.trimEnd())
        line = word.trimStart()
      } else {
        line += word
      }

      // A word wider than the box has to be cut, or it runs out of the shape.
      // This is checked AFTER the line break above rather than inside it: an
      // over-long word that happens to be first on its line never reaches that
      // branch, so a one-word sticky note ("Internationalization") used to
      // overflow while the same word after another word wrapped correctly.
      if (line.trim() !== '' && measure(line) > maxWidth) {
        let chunk = ''
        for (const ch of line) {
          if (chunk !== '' && measure(chunk + ch) > maxWidth) {
            out.push(chunk)
            chunk = ch
          } else {
            chunk += ch
          }
        }
        line = chunk
      }
    }
    out.push(line.trimEnd())
  }
  return out
}

// ---------------------------------------------------------------------------
// Below here needs a browser.
// ---------------------------------------------------------------------------

/** The resolved palette for one export. */
export interface RasterTheme {
  note: Record<NoteColor, { fill: string; ink: string }>
  background: string
  foreground: string
  muted: string
  fontFamily: string
}

const NOTE_COLORS: NoteColor[] = ['yellow', 'blue', 'green', 'pink', 'purple', 'orange', 'gray']

/**
 * Read the live palette out of the document.
 *
 * The note colors are `--note` / `--note-ink` triples set by a `.note-<color>`
 * class and redefined under `.dark`, so the only honest way to learn them is to
 * put an element carrying that class into the page and ask. Hardcoding the
 * fourteen values from index.css would mean every export came out in light mode
 * and drifted the first time someone retuned the palette — and `theme-check.mjs`
 * would not catch it, because it checks the CSS, not this file.
 *
 * The probe is attached to `document.body` rather than to the board, so it
 * inherits `.dark` from `<html>` wherever the board happens to be mounted.
 */
export function readRasterTheme(): RasterTheme {
  const probe = document.createElement('div')
  probe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;pointer-events:none'
  document.body.appendChild(probe)

  const root = getComputedStyle(document.documentElement)
  const hsl = (v: string) => (v.trim() ? `hsl(${v.trim()})` : 'transparent')

  const note = {} as RasterTheme['note']
  for (const color of NOTE_COLORS) {
    probe.className = `note-${color}`
    const cs = getComputedStyle(probe)
    note[color] = {
      fill: hsl(cs.getPropertyValue('--note')),
      ink: hsl(cs.getPropertyValue('--note-ink')),
    }
  }

  probe.className = ''
  const body = getComputedStyle(document.body)
  const theme: RasterTheme = {
    note,
    background: hsl(root.getPropertyValue('--background')),
    foreground: hsl(root.getPropertyValue('--foreground')),
    muted: hsl(root.getPropertyValue('--muted-foreground')),
    // Whatever the page is actually using, so an export matches the screen even
    // though the self-hosted Outfit woff2 is already loaded by the time anyone
    // can click a button.
    fontFamily: body.fontFamily || 'system-ui, sans-serif',
  }
  probe.remove()
  return theme
}

export interface DrawOptions {
  /** Loaded images, keyed by `extra.path`. Anything missing draws a placeholder. */
  images?: Map<string, CanvasImageSource>
  /** Paint the page background behind the objects. Off gives a transparent PNG. */
  opaque?: boolean
  theme?: RasterTheme
}

/**
 * Draw a selection onto a new canvas and return it.
 *
 * Objects are drawn back-to-front by `z`, with frames first — the same ordering
 * the canvas itself uses, so a note sitting on a frame keeps sitting on it.
 */
export function drawSelection(
  objects: BoardObject[],
  bounds: Rect,
  opts: DrawOptions = {},
): HTMLCanvasElement | null {
  const size = rasterSize(bounds)
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const theme = opts.theme ?? readRasterTheme()

  if (opts.opaque) {
    ctx.fillStyle = theme.background
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  ctx.scale(size.scale, size.scale)
  ctx.translate(-bounds.x, -bounds.y)
  ctx.textBaseline = 'top'

  const ordered = [...objects].sort(
    (a, b) => (a.kind === 'frame' ? -1 : b.kind === 'frame' ? 1 : 0) || a.z - b.z,
  )
  for (const o of ordered) drawObject(ctx, o, theme, opts)

  return canvas
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  o: BoardObject,
  theme: RasterTheme,
  opts: DrawOptions,
) {
  const palette = theme.note[o.color] ?? theme.note.gray

  if (o.kind === 'arrow') return drawArrow(ctx, o, palette.fill)
  if (o.kind === 'frame') return drawFrame(ctx, o, theme)
  if (o.kind === 'meeple') return drawMeeple(ctx, o, theme, palette)
  if (o.kind === 'image') return drawImage(ctx, o, theme, opts)
  if (o.kind === 'note' || o.kind === 'rect' || o.kind === 'ellipse' || o.kind === 'text') {
    return drawShape(ctx, o, theme, palette)
  }
  // Unknown kind: draw nothing. See the header.
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

function drawShape(
  ctx: CanvasRenderingContext2D,
  o: BoardObject,
  theme: RasterTheme,
  palette: { fill: string; ink: string },
) {
  const isText = o.kind === 'text'

  if (!isText) {
    ctx.fillStyle = palette.fill
    if (o.kind === 'ellipse') {
      ctx.beginPath()
      ctx.ellipse(o.x + o.width / 2, o.y + o.height / 2, o.width / 2, o.height / 2, 0, 0, Math.PI * 2)
      ctx.fill()
    } else {
      // note is rounded-sm (2px), rect is rounded-md (6px).
      roundedRect(ctx, o.x, o.y, o.width, o.height, o.kind === 'note' ? 2 : 6)
      ctx.fill()
    }
  }

  const text = o.text ?? ''
  if (!text.trim()) return

  // Matches the padding classes on screen: p-2.5 (10px) for a shape, p-1 (4px)
  // for a text object.
  const pad = isText ? 4 : 10
  const maxWidth = Math.max(1, o.width - pad * 2)
  const weight = isText ? '500 ' : ''
  ctx.font = `${weight}${o.font_size}px ${theme.fontFamily}`
  ctx.fillStyle = isText ? theme.foreground : palette.ink

  // leading-snug is 1.375.
  const lineHeight = o.font_size * 1.375
  const lines = wrapLines(text, maxWidth, (s) => ctx.measureText(s).width)

  const centred = o.kind === 'ellipse'
  const blockHeight = lines.length * lineHeight
  // A shape is items-start, an ellipse is centred both ways, a text object is
  // items-center.
  const top =
    centred || isText
      ? o.y + Math.max(pad, (o.height - blockHeight) / 2)
      : o.y + pad

  ctx.save()
  // Clip, because the on-screen container is overflow-hidden — text longer than
  // the shape is cut off there and must be cut off here too.
  ctx.beginPath()
  ctx.rect(o.x, o.y, o.width, o.height)
  ctx.clip()
  ctx.textAlign = centred ? 'center' : 'left'
  const x = centred ? o.x + o.width / 2 : o.x + pad
  lines.forEach((line, i) => ctx.fillText(line, x, top + i * lineHeight))
  ctx.restore()
  ctx.textAlign = 'left'
}

function drawArrow(ctx: CanvasRenderingContext2D, o: BoardObject, color: string) {
  const len = Math.max(Math.hypot(o.width, o.height), 8)
  const angle = Math.atan2(o.height, o.width)

  ctx.save()
  ctx.translate(o.x, o.y)
  ctx.rotate(angle)
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = 3
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(len - 8, 0)
  ctx.stroke()
  // The head, matching the polygon in board-canvas.tsx: tip at the full length,
  // base 12 back and 6 either side of the axis.
  ctx.beginPath()
  ctx.moveTo(len, 0)
  ctx.lineTo(len - 12, -6)
  ctx.lineTo(len - 12, 6)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

function drawFrame(ctx: CanvasRenderingContext2D, o: BoardObject, theme: RasterTheme) {
  ctx.save()
  ctx.strokeStyle = theme.muted
  ctx.globalAlpha = 0.5
  ctx.lineWidth = 2
  ctx.setLineDash([6, 4])
  roundedRect(ctx, o.x, o.y, o.width, o.height, 8)
  ctx.stroke()
  ctx.restore()

  const label = o.text?.trim() || 'Frame'
  ctx.font = `500 12px ${theme.fontFamily}`
  ctx.fillStyle = theme.muted
  // -top-6 on screen; the label's baseline sits 20 above the frame.
  ctx.fillText(label, o.x, o.y - 20)
}

function drawMeeple(
  ctx: CanvasRenderingContext2D,
  o: BoardObject,
  theme: RasterTheme,
  palette: { fill: string; ink: string },
) {
  // The figure is authored in a 100x100 viewBox with preserveAspectRatio
  // xMidYMid meet, so it is drawn at the largest square that fits and centred.
  const side = Math.min(o.width, o.height)
  const s = side / 100
  ctx.save()
  ctx.translate(o.x + (o.width - side) / 2, o.y + (o.height - side) / 2)
  ctx.scale(s, s)
  ctx.fillStyle = palette.fill
  ctx.strokeStyle = palette.ink
  ctx.globalAlpha = 1
  ctx.lineWidth = 2.5

  ctx.beginPath()
  ctx.arc(50, 19, 15, 0, Math.PI * 2)
  ctx.fill()
  ctx.save()
  ctx.globalAlpha = 0.45
  ctx.stroke()
  ctx.restore()

  // The same path as MeepleFigure in board-canvas.tsx, as Path2D rather than
  // retraced by hand — one copy of the geometry, so the two renderers cannot
  // disagree about the shape of a person.
  const body = new Path2D(
    'M50 35 C62 35 71 43 75 53 L90 65 C95 69 93 77 86 77 L71 77 C67 77 64 75 62 71 C64 82 66 91 67 96 C67 99 65 100 62 100 L38 100 C35 100 33 99 33 96 C34 91 36 82 38 71 C36 75 33 77 29 77 L14 77 C7 77 5 69 10 65 L25 53 C29 43 38 35 50 35 Z',
  )
  ctx.fill(body)
  ctx.save()
  ctx.globalAlpha = 0.45
  ctx.stroke(body)
  ctx.restore()
  ctx.restore()

  const name = o.text?.trim()
  if (!name) return

  // Below the token, centred, wrapping to the 190% width the span uses.
  ctx.font = `500 12px ${theme.fontFamily}`
  ctx.fillStyle = theme.foreground
  ctx.textAlign = 'center'
  const maxWidth = o.width * 1.9
  const lines = wrapLines(name, maxWidth, (s) => ctx.measureText(s).width)
  const cx = o.x + o.width / 2
  lines.forEach((line, i) => ctx.fillText(line, cx, o.y + o.height + 6 + i * 15))
  ctx.textAlign = 'left'
}

function drawImage(
  ctx: CanvasRenderingContext2D,
  o: BoardObject,
  theme: RasterTheme,
  opts: DrawOptions,
) {
  const path = typeof o.extra?.path === 'string' ? o.extra.path : null
  const img = path ? opts.images?.get(path) : undefined

  ctx.save()
  roundedRect(ctx, o.x, o.y, o.width, o.height, 2)
  ctx.clip()

  if (img) {
    // object-contain: fit inside the box, centred, aspect preserved.
    const iw = 'naturalWidth' in img ? img.naturalWidth : (img as HTMLCanvasElement).width
    const ih = 'naturalHeight' in img ? img.naturalHeight : (img as HTMLCanvasElement).height
    if (iw > 0 && ih > 0) {
      const k = Math.min(o.width / iw, o.height / ih)
      const w = iw * k
      const h = ih * k
      ctx.drawImage(img, o.x + (o.width - w) / 2, o.y + (o.height - h) / 2, w, h)
    }
  } else {
    // An image whose URL has not been signed yet, or whose file is gone. A
    // visible box matches the on-screen placeholder; drawing nothing would make
    // a copied board look like it had a hole in it.
    ctx.fillStyle = theme.muted
    ctx.globalAlpha = 0.15
    ctx.fillRect(o.x, o.y, o.width, o.height)
  }
  ctx.restore()
}

/**
 * Load every image a selection needs, so `drawSelection` can stay synchronous.
 *
 * The URLs are the signed ones the page already holds. They are served by this
 * tenant's own Caddy, so they are same-origin and the canvas is never tainted —
 * which is what makes `toBlob` work at all. If storage is ever moved to a
 * different host, these need `crossOrigin = 'anonymous'` AND that host needs to
 * send CORS headers, or the export fails at the copy rather than here.
 *
 * A failed load resolves rather than rejects: one broken image should cost its
 * own rectangle, not the whole export.
 */
export async function loadBoardImages(
  objects: BoardObject[],
  urlFor: (path: string) => string | undefined,
): Promise<Map<string, CanvasImageSource>> {
  const paths = [
    ...new Set(
      objects
        .filter((o) => o.kind === 'image')
        .map((o) => (typeof o.extra?.path === 'string' ? o.extra.path : null))
        .filter((p): p is string => Boolean(p)),
    ),
  ]
  const loaded = new Map<string, CanvasImageSource>()
  await Promise.all(
    paths.map(
      (path) =>
        new Promise<void>((resolve) => {
          const url = urlFor(path)
          if (!url) return resolve()
          const img = new Image()
          img.onload = () => {
            loaded.set(path, img)
            resolve()
          }
          img.onerror = () => resolve()
          img.src = url
        }),
    ),
  )
  return loaded
}
